import * as React from 'react';
import type { IScannerControls } from '@zxing/browser';

export type ScannerError = 'insecure' | 'unsupported' | 'permission' | 'noCamera' | 'unknown';

type Options = {
  /** Raw decoded text. Fires once per scan; the caller closes the scanner. */
  onDecoded: (value: string) => void;
  /** Restrict to one camera. Undefined lets the browser pick (prefers the rear one). */
  deviceId?: string;
};

/** Secure context is a hard requirement for getUserMedia — http:// never gets a camera. */
const isSecure = () => window.isSecureContext || window.location.hostname === 'localhost';

const hasMediaDevices = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

const classifyError = (error: unknown): ScannerError => {
  const name = (error as DOMException)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'permission';
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') return 'noCamera';
  return 'unknown';
};

/**
 * Runs a continuous QR/barcode decode over a live camera preview.
 *
 * The formats mirror the mobile app's scanners: QR for the device code and
 * Code128/39/93 for the IMEI barcode printed on cellular devices.
 *
 * @zxing/browser is loaded dynamically so the decoder (a few hundred KB) only
 * reaches the wire when someone actually opens a scanner.
 */
export function useCameraScanner({ onDecoded, deviceId }: Options) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [error, setError] = React.useState<ScannerError>();
  const [isReady, setIsReady] = React.useState(false);
  const [cameras, setCameras] = React.useState<MediaDeviceInfo[]>([]);
  // Which camera the browser actually gave us — only known after the stream is
  // up, and not necessarily the first in the list when we let it choose.
  const [activeDeviceId, setActiveDeviceId] = React.useState<string>();

  // Kept in a ref so a new callback identity doesn't tear the camera down.
  const onDecodedRef = React.useRef(onDecoded);
  onDecodedRef.current = onDecoded;

  React.useEffect(() => {
    if (!isSecure()) {
      setError('insecure');
      return;
    }
    if (!hasMediaDevices()) {
      setError('unsupported');
      return;
    }

    let controls: IScannerControls | undefined;
    let cancelled = false;
    setIsReady(false);

    (async () => {
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.QR_CODE,
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_93,
        ]);
        // Barcodes on device labels are small and often slightly rotated; the
        // extra passes cost CPU but make a handheld scan land first try.
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 100 });

        const onResult = (result?: { getText: () => string }) => {
          if (cancelled || !result) return;
          const text = result.getText().trim();
          if (text) onDecodedRef.current(text);
        };

        controls = deviceId
          ? await reader.decodeFromVideoDevice(deviceId, videoRef.current ?? undefined, onResult)
          : // No explicit device: ask for the rear camera. facingMode is a
            // preference rather than `exact`, so a laptop with only a front
            // camera still gets a stream instead of an OverconstrainedError.
            await reader.decodeFromConstraints(
              { video: { facingMode: 'environment' } },
              videoRef.current ?? undefined,
              onResult
            );

        if (cancelled) {
          controls.stop();
          return;
        }
        setIsReady(true);

        const stream = videoRef.current?.srcObject;
        if (stream instanceof MediaStream) {
          setActiveDeviceId(stream.getVideoTracks()[0]?.getSettings().deviceId);
        }

        // Labels are only populated once permission has been granted, so this
        // has to run after the stream is up or the picker shows blank entries.
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          if (!cancelled) setCameras(devices.filter((d) => d.kind === 'videoinput'));
        } catch {
          // Picker is a convenience; scanning works without it.
        }
      } catch (e) {
        if (!cancelled) setError(classifyError(e));
      }
    })();

    return () => {
      cancelled = true;
      controls?.stop();
    };
  }, [deviceId]);

  return { videoRef, error, isReady, cameras, activeDeviceId };
}
